import React, { useState, useEffect } from 'react';

import { themeColors } from '../../../style/color';
import axios from 'axios';
import { mainurl } from '../../../Mainurl';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Input,
    Spinner,
    Badge,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Button,
    ButtonGroup,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ListGroup,
    ListGroupItem
} from 'reactstrap';
import {
    BsDownload,
    BsGrid3X3Gap,
    BsList,
    BsBoxSeam,
    BsScissors,
    BsGraphUp,
    BsBarChart,
    BsArrowUpRight,
    BsArrowDownRight
} from 'react-icons/bs';
import { GiWheat } from 'react-icons/gi'
import { BsPeople } from 'react-icons/bs';
import { Bar, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
);


// Enhanced chart options
const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: true,
            position: 'top',
            labels: {
                usePointStyle: true,
                padding: 20,
                font: {
                    size: 12,
                    weight: '500'
                }
            }
        },
        tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: themeColors.primary,
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: false,
        }
    },
    scales: {
        x: {
            grid: {
                display: false
            },
            ticks: {
                font: {
                    size: 11,
                    weight: '500'
                }
            }
        },
        y: {
            beginAtZero: true,
            grid: {
                color: 'rgba(0, 0, 0, 0.1)'
            },
            ticks: {
                font: {
                    size: 11
                }
            }
        }
    },
    elements: {
        bar: {
            borderRadius: 6,
            borderSkipped: false,
        }
    }
};

const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: true,
            position: 'top',
        },
    },
    scales: {
        x: {
            grid: {
                display: false
            }
        },
        y: {
            beginAtZero: true,
            grid: {
                color: 'rgba(0, 0, 0, 0.1)'
            }
        }
    }
};

const CropGraphicalReport = () => {
    const [loading, setLoading] = useState(true);
    const [organization, setOrganization] = useState('116');
    const [activeTab, setActiveTab] = useState('overview');
    const [viewMode, setViewMode] = useState('grid');
    const [comparisonModal, setComparisonModal] = useState(false);
    const [selectedOrgs, setSelectedOrgs] = useState([]);
    const [comparisonData, setComparisonData] = useState(null);

    const [distributionGraphData, setDistributionGraphData] = useState([]);
    const [trainingGraphData, setTrainingGraphData] = useState([]);
    const [sowingGraphData, setSowingGraphData] = useState([]);
    const [harvestingGraphData, setHarvestingGraphData] = useState([]);
    const [rawData, setRawData] = useState({});
    const [orgList, setOrgList] = useState([]);

    useEffect(() => {
        get_graph();
    }, [organization]);

    const processDataForCharts = (distribution, training, sowing, harvesting) => {
        // Get unique organization IDs and their names
        const orgMap = {};
        
        // Process distribution data to get org names
        distribution.forEach(item => {
            if (!orgMap[item.org_id]) {
                orgMap[item.org_id] = item.org_name;
            }
        });
        
        // Process training data to get org names (if any)
        training.forEach(item => {
            const orgId = item.org_id || item.org_code;
            if (!orgMap[orgId] && item.org_name) {
                orgMap[orgId] = item.org_name;
            }
        });
        
        // Process sowing data to get org names (if any)
        sowing.forEach(item => {
            if (!orgMap[item.org_id] && item.org_name) {
                orgMap[item.org_id] = item.org_name;
            }
        });
        
        // Process harvesting data to get org names (if any)
        harvesting.forEach(item => {
            if (!orgMap[item.org_id] && item.org_name) {
                orgMap[item.org_id] = item.org_name;
            }
        });

        const orgIds = Object.keys(orgMap);
        setOrgList(Object.entries(orgMap).map(([id, name]) => ({ id, name })));

        // Process distribution data - group by org_id
        const distributionByOrg = {};
        distribution.forEach(item => {
            if (!distributionByOrg[item.org_id]) {
                distributionByOrg[item.org_id] = 0;
            }
            distributionByOrg[item.org_id] += parseInt(item.total_distribution || 0);
        });

        // Process training data - group by org_id or org_code
        const trainingByOrg = {};
        training.forEach(item => {
            const orgId = item.org_id || item.org_code;
            if (!trainingByOrg[orgId]) {
                trainingByOrg[orgId] = 0;
            }
            trainingByOrg[orgId] += parseInt(item.total_farmer || 0);
        });

        // Process sowing data - group by org_id
        const sowingByOrg = {};
        sowing.forEach(item => {
            if (!sowingByOrg[item.org_id]) {
                sowingByOrg[item.org_id] = 0;
            }
            sowingByOrg[item.org_id] += parseInt(item.total_area || 0);
        });

        // Process harvesting data - group by org_id and extract numbers from yield strings
        const harvestingByOrg = {};
        harvesting.forEach(item => {
            if (!harvestingByOrg[item.org_id]) {
                harvestingByOrg[item.org_id] = 0;
            }
            const yieldValue = item.crop_yield ? parseInt(item.crop_yield.split('(')[0].trim()) || 0 : 0;
            harvestingByOrg[item.org_id] += yieldValue;
        });

        // Create arrays of data for each organization
        const distributionData = orgIds.map(orgId => distributionByOrg[orgId] || 0);
        const trainingData = orgIds.map(orgId => trainingByOrg[orgId] || 0);
        const sowingData = orgIds.map(orgId => sowingByOrg[orgId] || 0);
        const harvestingData = orgIds.map(orgId => harvestingByOrg[orgId] || 0);

        // Create chart datasets
        const distributionChart = {
            labels: orgIds.map(id => orgMap[id]),
            datasets: [{
                label: "Distribution",
                data: distributionData,
                backgroundColor: `${themeColors.primary}cc`,
                borderColor: themeColors.primary,
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            }]
        };

        const trainingChart = {
            labels: orgIds.map(id => orgMap[id]),
            datasets: [{
                label: "Farmers Trained",
                data: trainingData,
                backgroundColor: `${themeColors.secondary}cc`,
                borderColor: themeColors.secondary,
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            }]
        };

        const sowingChart = {
            labels: orgIds.map(id => orgMap[id]),
            datasets: [{
                label: "Area Sown",
                data: sowingData,
                backgroundColor: `${themeColors.info}cc`,
                borderColor: themeColors.info,
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            }]
        };

        const harvestingChart = {
            labels: orgIds.map(id => orgMap[id]),
            datasets: [{
                label: "Crop Yield",
                data: harvestingData,
                backgroundColor: `${themeColors.danger}cc`,
                borderColor: themeColors.danger,
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            }]
        };

        return {
            distributionChart,
            trainingChart,
            sowingChart,
            harvestingChart,
            totals: {
                distribution: distributionData.reduce((a, b) => a + b, 0),
                training: trainingData.reduce((a, b) => a + b, 0),
                sowing: sowingData.reduce((a, b) => a + b, 0),
                harvesting: harvestingData.reduce((a, b) => a + b, 0)
            },
            orgData: orgIds.map(id => ({
                id,
                name: orgMap[id],
                distribution: distributionByOrg[id] || 0,
                training: trainingByOrg[id] || 0,
                sowing: sowingByOrg[id] || 0,
                harvesting: harvestingByOrg[id] || 0
            }))
        };
    };

    const get_graph = async () => {
        setLoading(true);
        const postData = [{
            "org_id": sessionStorage.getItem('org_id'),
            "user_code": sessionStorage.getItem('user_id'),
            "password": sessionStorage.getItem('user_pass')
        }];

        try {
            // Execute all API calls concurrently
            const [distributionResponse, trainingResponse, sowingResponse, harvestingResponse] = await Promise.all([
                axios({
                    method: 'post',
                    url: mainurl + 'Dashboard/db_distribution_crop_1',
                    data: postData,
                    headers: { 'Content-Type': 'application/json' }
                }),
                axios({
                    method: 'post',
                    url: mainurl + 'Dashboard/db_training',
                    data: postData,
                    headers: { 'Content-Type': 'application/json' }
                }),
                axios({
                    method: 'post',
                    url: mainurl + 'Dashboard/db_sowing_1',
                    data: postData,
                    headers: { 'Content-Type': 'application/json' }
                }),
                axios({
                    method: 'post',
                    url: mainurl + 'Dashboard/db_harvesting',
                    data: postData,
                    headers: { 'Content-Type': 'application/json' }
                })
            ]);

            // Extract data from responses
            const distribution = distributionResponse.data;
            const training = trainingResponse.data;
            const sowing = sowingResponse.data;
            const harvesting = harvestingResponse.data;

            // Process the data
            const processedData = processDataForCharts(distribution, training, sowing, harvesting);

            // Update state with processed data
            setDistributionGraphData(processedData.distributionChart);
            setTrainingGraphData(processedData.trainingChart);
            setSowingGraphData(processedData.sowingChart);
            setHarvestingGraphData(processedData.harvestingChart);

            setRawData({
                distribution,
                training,
                sowing,
                harvesting,
                totals: processedData.totals,
                orgData: processedData.orgData
            });

        } catch (error) {
            console.error('Error fetching graph data:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleComparisonModal = () => {
        setComparisonModal(!comparisonModal);
        if (!comparisonModal) {
            setSelectedOrgs([]);
            setComparisonData(null);
        }
    };

    const handleOrgSelection = (orgId) => {
        if (selectedOrgs.includes(orgId)) {
            setSelectedOrgs(selectedOrgs.filter(id => id !== orgId));
        } else if (selectedOrgs.length < 3) {
            setSelectedOrgs([...selectedOrgs, orgId]);
        }
    };

    const generateComparison = () => {
        if (selectedOrgs.length < 2) return;
        
        const selectedOrgData = rawData.orgData.filter(org => selectedOrgs.includes(org.id));
        
        const comparisonChartData = {
            labels: ['Seed Distribution (kg)', 'Farmers Trained', 'Area Sown (acre)', 'Crop Yield (kg)'],
            datasets: selectedOrgData.map((org, index) => ({
                label: org.name,
                data: [org.distribution, org.training, org.sowing, org.harvesting],
                backgroundColor: [
                    `${themeColors.primary}${index === 0 ? 'cc' : '77'}`,
                    `${themeColors.secondary}${index === 0 ? 'cc' : '77'}`,
                    `${themeColors.info}${index === 0 ? 'cc' : '77'}`,
                    `${themeColors.danger}${index === 0 ? 'cc' : '77'}`
                ],
                borderColor: [
                    themeColors.primary,
                    themeColors.secondary,
                    themeColors.info,
                    themeColors.danger
                ],
                borderWidth: 1,
            }))
        };
        
        setComparisonData(comparisonChartData);
    };

    const totalDistribution = rawData.totals?.distribution || 0;
    const totalTraining = rawData.totals?.training || 0;
    const totalSowing = rawData.totals?.sowing || 0;
    const totalHarvesting = rawData.totals?.harvesting || 0;

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const ChartCard = ({ title, icon: Icon, data, total, color, gradient, description }) => (
        <Card className="h-100 shadow-sm border-0">
            <CardHeader className="bg-gradient" style={{ background: gradient }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <Icon size={20} className="me-2" style={{ color: color }} />
                        <div>
                            <CardTitle className="mb-0 fw-semibold">{title}</CardTitle>
                            <small className="text-muted">{description}</small>
                        </div>
                    </div>
                    <div className="text-end">
                        <h4 className="mb-0 fw-bold" style={{ color: color }}>{total}</h4>
                        <small className="text-muted">Total</small>
                    </div>
                </div>
            </CardHeader>
            <CardBody>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '250px' }}>
                        <Spinner style={{ color: color }} />
                    </div>
                ) : (
                    <div style={{ height: '250px' }}>
                        <Bar options={barChartOptions} data={data} />
                    </div>
                )}
            </CardBody>
        </Card>
    );

    // Calculate performance metrics
    const performanceMetrics = rawData.orgData ? rawData.orgData.map(org => {
        const performanceScore = (
            (org.distribution / Math.max(...rawData.orgData.map(o => o.distribution)) * 25) +
            (org.training / Math.max(...rawData.orgData.map(o => o.training || 1)) * 25) +
            (org.sowing / Math.max(...rawData.orgData.map(o => o.sowing || 1)) * 25) +
            (org.harvesting / Math.max(...rawData.orgData.map(o => o.harvesting || 1)) * 25)
        );
        
        return {
            ...org,
            performanceScore: Math.round(performanceScore)
        };
    }).sort((a, b) => b.performanceScore - a.performanceScore) : [];

    return (
        <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1 fw-bold d-flex align-items-center">
                                <GiWheat className="me-2" style={{ color: themeColors.success }} />
                                Crop Analysis
                            </h5>
                            <p className="text-muted mb-0">Performance overview across organizations for cultivation</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <Badge color="success" pill className="px-3 py-2">
                                Live Data
                            </Badge>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Key Metrics Summary */}
            <Row className="mb-4">
                <Col lg={3} md={6} className="mb-3">
                    <Card className="border-0 shadow-sm bg-primary text-white">
                        <CardBody className="text-center">
                            <BsBoxSeam size={24} className="mb-2" />
                            <h4 className="mb-1">{totalDistribution.toLocaleString()} kg</h4>
                            <small>Total Seeds Distributed</small>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} className="mb-3">
                    <Card className="border-0 shadow-sm bg-secondary text-white">
                        <CardBody className="text-center">
                            <BsPeople size={24} className="mb-2" />
                            <h4 className="mb-1">{totalTraining.toLocaleString()}</h4>
                            <small>Farmers Trained</small>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} className="mb-3">
                    <Card className="border-0 shadow-sm bg-info text-white">
                        <CardBody className="text-center">
                            <GiWheat size={24} className="mb-2" />
                            <h4 className="mb-1">{totalSowing.toLocaleString()} acre</h4>
                            <small>Total Area Sown</small>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} className="mb-3">
                    <Card className="border-0 shadow-sm bg-danger text-white">
                        <CardBody className="text-center">
                            <BsScissors size={24} className="mb-2" />
                            <h4 className="mb-1">{totalHarvesting.toLocaleString()} kg</h4>
                            <small>Total Yield</small>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Navigation and Controls */}
            <Row className="mb-4">
                <Col>
                    <Card className="border-0 shadow-sm">
                        <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <Nav tabs className="border-0">
                                    <NavItem>
                                        <NavLink
                                            className={`${activeTab === 'overview' ? 'active' : ''} fw-semibold px-4`}
                                            onClick={() => toggleTab('overview')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <BsGrid3X3Gap className="me-2" />
                                            Overview
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={`${activeTab === 'summary' ? 'active' : ''} fw-semibold px-4`}
                                            onClick={() => toggleTab('summary')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <BsBarChart className="me-2" />
                                            Summary
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <div className="d-flex align-items-center gap-2">
                                    <ButtonGroup size="sm">
                                        <Button
                                            color={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                                            onClick={() => setViewMode('grid')}
                                        >
                                            <BsGrid3X3Gap />
                                        </Button>
                                        <Button
                                            color={viewMode === 'list' ? 'primary' : 'outline-primary'}
                                            onClick={() => setViewMode('list')}
                                        >
                                            <BsList />
                                        </Button>
                                    </ButtonGroup>
                                    <Button color="outline-primary" size="sm" onClick={toggleComparisonModal}>
                                        <BsGraphUp className="me-1" />
                                        Compare
                                    </Button>
                                    <Button color="outline-primary" size="sm">
                                        <BsDownload className="me-1" />
                                        Export
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <TabContent activeTab={activeTab}>
                <TabPane tabId="overview">
                    {/* Individual Charts */}
                    <Row className={viewMode === 'grid' ? 'mb-4' : ''}>
                        {viewMode === 'grid' ? (
                            <>
                                <Col lg={6} className="mb-4">
                                    <ChartCard
                                        title="Seed Distribution"
                                        icon={BsBoxSeam}
                                        data={distributionGraphData}
                                        total={`${totalDistribution.toLocaleString()} kg`}
                                        color={themeColors.primary}
                                        gradient="linear-gradient(135deg, #3b82f620 0%, #1e40af10 100%)"
                                        description="Distributed by organization"
                                    />
                                </Col>
                                <Col lg={6} className="mb-4">
                                    <ChartCard
                                        title="Training Programs"
                                        icon={BsPeople}
                                        data={trainingGraphData}
                                        total={totalTraining.toLocaleString()}
                                        color={themeColors.secondary}
                                        gradient="linear-gradient(135deg, #8b5cf620 0%, #7c3aed10 100%)"
                                        description="Farmers trained"
                                    />
                                </Col>
                                <Col lg={6} className="mb-4">
                                    <ChartCard
                                        title="Sowing Activity"
                                        icon={GiWheat}
                                        data={sowingGraphData}
                                        total={`${totalSowing.toLocaleString()} acre`}
                                        color={themeColors.info}
                                        gradient="linear-gradient(135deg, #06b6d420 0%, #0891b210 100%)"
                                        description="Total area sown"
                                    />
                                </Col>
                                <Col lg={6} className="mb-4">
                                    <ChartCard
                                        title="Harvest Yield"
                                        icon={BsScissors}
                                        data={harvestingGraphData}
                                        total={`${totalHarvesting.toLocaleString()} kg`}
                                        color={themeColors.danger}
                                        gradient="linear-gradient(135deg, #ef444420 0%, #dc262610 100%)"
                                        description="Yield harvested"
                                    />
                                </Col>
                            </>
                        ) : (
                            <Col>
                                <Card className="shadow-sm border-0">
                                    <CardBody>
                                        <table className="table table-responsive table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Organization</th>
                                                    <th>Seeds Distributed</th>
                                                    <th>Farmers Trained</th>
                                                    <th>Area Sown</th>
                                                    <th>Crop Yield</th>
                                                    <th>Performance Score</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {performanceMetrics.map(org => (
                                                    <tr key={org.id}>
                                                        <td className="fw-semibold">{org.name}</td>
                                                        <td>{org.distribution.toLocaleString()}</td>
                                                        <td>{org.training.toLocaleString()}</td>
                                                        <td>{org.sowing.toLocaleString()}</td>
                                                        <td>{org.harvesting.toLocaleString()}</td>
                                                        <td>
                                                            <Badge color={
                                                                org.performanceScore >= 80 ? 'success' :
                                                                org.performanceScore >= 60 ? 'warning' : 'danger'
                                                            }>
                                                                {org.performanceScore}/100
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </CardBody>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </TabPane>
                
                <TabPane tabId="summary">
                    <Row>
                        <Col lg={8}>
                            <Card className="shadow-sm border-0 mb-4">
                                <CardHeader className="bg-light">
                                    <CardTitle className="mb-0 fw-semibold d-flex align-items-center">
                                        <BsBarChart className="me-2" />
                                        Overall Performance Summary
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                                            <Spinner />
                                        </div>
                                    ) : (
                                        <Row>
                                            <Col md={6}>
                                                <h6 className="mb-3">Key Metrics Distribution</h6>
                                                <ListGroup flush>
                                                    <ListGroupItem className="d-flex justify-content-between align-items-center">
                                                        <span>Seeds Distribution Efficiency</span>
                                                        <Badge color="primary" pill>
                                                            {rawData.orgData ? Math.round((totalDistribution / rawData.orgData.length) / 1000) : 0} kg/org
                                                        </Badge>
                                                    </ListGroupItem>
                                                    <ListGroupItem className="d-flex justify-content-between align-items-center">
                                                        <span>Training Reach</span>
                                                        <Badge color="secondary" pill>
                                                            {rawData.orgData ? Math.round(totalTraining / rawData.orgData.length) : 0} farmers/org
                                                        </Badge>
                                                    </ListGroupItem>
                                                    <ListGroupItem className="d-flex justify-content-between align-items-center">
                                                        <span>Land Utilization</span>
                                                        <Badge color="info" pill>
                                                            {rawData.orgData ? Math.round(totalSowing / rawData.orgData.length) : 0} acre/org
                                                        </Badge>
                                                    </ListGroupItem>
                                                    <ListGroupItem className="d-flex justify-content-between align-items-center">
                                                        <span>Yield Efficiency</span>
                                                        <Badge color="danger" pill>
                                                            {rawData.orgData && totalSowing > 0 ? Math.round(totalHarvesting / totalSowing) : 0} kg/acre
                                                        </Badge>
                                                    </ListGroupItem>
                                                </ListGroup>
                                            </Col>
                                            <Col md={6}>
                                                <h6 className="mb-3">Performance Highlights</h6>
                                                {performanceMetrics.length > 0 && (
                                                    <>
                                                        <div className="d-flex align-items-center mb-3">
                                                            <div className="bg-success rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                                <span className="text-white fw-bold">1</span>
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold">{performanceMetrics[0].name}</div>
                                                                <small className="text-muted">Top performer</small>
                                                            </div>
                                                            <Badge color="success" className="ms-auto">
                                                                {performanceMetrics[0].performanceScore}/100
                                                            </Badge>
                                                        </div>
                                                        
                                                        {performanceMetrics.length > 1 && (
                                                            <div className="d-flex align-items-center mb-3">
                                                                <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                                    <span className="text-white fw-bold">2</span>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-semibold">{performanceMetrics[1].name}</div>
                                                                    <small className="text-muted">Second best</small>
                                                                </div>
                                                                <Badge color="warning" className="ms-auto">
                                                                    {performanceMetrics[1].performanceScore}/100
                                                                </Badge>
                                                            </div>
                                                        )}
                                                        
                                                        {performanceMetrics.length > 2 && (
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-danger rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                                    <span className="text-white fw-bold">{performanceMetrics.length}</span>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-semibold">{performanceMetrics[performanceMetrics.length - 1].name}</div>
                                                                    <small className="text-muted">Needs improvement</small>
                                                                </div>
                                                                <Badge color="danger" className="ms-auto">
                                                                    {performanceMetrics[performanceMetrics.length - 1].performanceScore}/100
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </Col>
                                        </Row>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                        
                        <Col lg={4}>
                            <Card className="shadow-sm border-0 mb-4">
                                <CardHeader className="bg-light">
                                    <CardTitle className="mb-0 fw-semibold">Yield Efficiency</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                                            <Spinner />
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <h3 className={`fw-bold ${totalSowing > 0 ? (totalHarvesting / totalSowing) > 500 ? 'text-success' : 'text-warning' : 'text-muted'}`}>
                                                {totalSowing > 0 ? Math.round(totalHarvesting / totalSowing) : 0}
                                            </h3>
                                            <p className="text-muted mb-2">kg per acre</p>
                                            <div className="d-flex align-items-center justify-content-center">
                                                {totalSowing > 0 && (totalHarvesting / totalSowing) > 500 ? (
                                                    <>
                                                        <BsArrowUpRight className="text-success me-1" />
                                                        <small className="text-success">Good yield efficiency</small>
                                                    </>
                                                ) : (
                                                    <>
                                                        <BsArrowDownRight className="text-warning me-1" />
                                                        <small className="text-warning">Can be improved</small>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                            
                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-light">
                                    <CardTitle className="mb-0 fw-semibold">Training Impact</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                                            <Spinner />
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <h3 className={`fw-bold ${totalTraining > 0 ? (totalSowing / totalTraining) > 2 ? 'text-success' : 'text-info' : 'text-muted'}`}>
                                                {totalTraining > 0 ? (totalSowing / totalTraining).toFixed(1) : 0}
                                            </h3>
                                            <p className="text-muted mb-2">acres per trained farmer</p>
                                            <small className="text-muted">
                                                {totalTraining > 0 ? `${totalTraining} farmers trained on ${totalSowing} acres` : 'No training data'}
                                            </small>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </TabPane>
            </TabContent>

            {/* Comparison Modal */}
            <Modal isOpen={comparisonModal} toggle={toggleComparisonModal} size="lg">
                <ModalHeader toggle={toggleComparisonModal}>
                    <BsGraphUp className="me-2" />
                    Compare Organizations
                </ModalHeader>
                <ModalBody>
                    <p className="text-muted">Select up to 3 organizations to compare their performance</p>
                    
                    <Row className="mb-3">
                        {orgList.map(org => (
                            <Col md={6} key={org.id} className="mb-2">
                                <div 
                                    className={`p-2 border rounded cursor-pointer ${selectedOrgs.includes(org.id) ? 'border-primary bg-light' : ''}`}
                                    onClick={() => handleOrgSelection(org.id)}
                                >
                                    <div className="form-check">
                                        <Input 
                                            type="checkbox" 
                                            checked={selectedOrgs.includes(org.id)} 
                                            onChange={() => {}} 
                                            className="form-check-input" 
                                        />
                                        <label className="form-check-label">{org.name}</label>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                    
                    {comparisonData && (
                        <div className="mt-4">
                            <h6>Comparison Chart</h6>
                            <div style={{ height: '300px' }}>
                                <Bar data={comparisonData} options={barChartOptions} />
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleComparisonModal}>
                        Cancel
                    </Button>
                    <Button 
                        color="primary" 
                        onClick={generateComparison}
                        disabled={selectedOrgs.length < 2}
                    >
                        Generate Comparison
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CropGraphicalReport;